/**
 * SettingsModal.tsx — the consolidated settings surface.
 *
 * Replaces the old MenuBar "Settings" dropdown. Everything that was scattered
 * across that dropdown (tick rate, connection, reset) plus the theme toggle
 * lives here as one tabbed modal:
 *
 *   - General    — theme + reset-to-step-0
 *   - Engine     — tick rate (hz)
 *   - Connection — API label + status + disconnect (only when wired to a backend)
 *
 * The MenuBar's "Settings…" entry opens this. The modal is a controlled
 * component: App owns the open state + all the setting values + their setters,
 * so a change applies immediately (no Apply button — same immediate-apply
 * semantics the old dropdown had).
 */
import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import type { Theme } from './MenuBar';
import type { ConnectionStatus } from '../engine/ApiAdapter';
import {
  ArrowCounterClockwise,
  Check,
  Clock,
  Copy,
  Desktop,
  FileText,
  Key,
  Lightning,
  Lock,
  Moon,
  PencilSimple,
  Plugs,
  Plus,
  Sparkle,
  Sun,
  Trash,
} from '@phosphor-icons/react';
import {
  type AiConfig,
  type AiPreset,
  type AiProvider,
  type EmbeddingsConfig,
  type EmbeddingsTestResult,
  createAiProvider,
  deleteAiProvider,
  fetchEmbeddingsConfig,
  saveEmbeddingsConfig,
  testAiProvider,
  testEmbeddingsConfig,
  updateAiProvider,
} from '../chat/aiConfig';
import { useConfirm } from './ConfirmDialog';
import { CronTab } from './CronTab';
import { VskiSetupPanel } from './VskiSetupPanel';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  // General
  theme: Theme;
  onToggleTheme: () => void;
  canReset: boolean;
  onReset: () => void;
  // Engine
  hz: number;
  onSetHz: (hz: number) => void;
  // Engine (backend-gated) — the remembered "stop services on close"
  // choice. Present only when a backend project is open.
  stopServicesOnClose?: boolean | null;
  onSetStopServices?: (v: boolean | null) => void;
  // Engine (backend-gated) — whether the project's workflow worker starts
  // automatically when the project opens. Persisted in the project manifest.
  workflowsAutostart?: boolean;
  onSetWorkflowsAutostart?: (v: boolean) => void;
  // Connection
  connectionStatus?: ConnectionStatus;
  apiLabel?: string;
  onDisconnect?: () => void;
  // Storage (backend-gated) — the VSKI platform setup/management surface
  // (same panel the Connect-API wizard's step 2 embeds). Status + key minting
  // + manual paste + clear, all brokered by the backend.
  storageBackendOrigin?: string | null;
  storageToken?: string | null;
  // Cron (backend + open project) — per-project cron management: scheduled
  // task webhooks + hook settings. Workflow schedules live in the trigger
  // editor; this tab manages the project-level task jobs.
  cronProjectId?: string;
  cronBackendOrigin?: string | null;
  cronToken?: string | null;
  // Connection (backend-gated) — the container host probe's target. Present
  // only when a backend HTTP origin is wired; the row hides otherwise.
  connectionBackendOrigin?: string | null;
  // AI (backend-gated) — provider/model picker. Keys are server-side; this tab
  // only picks which provider+model the chat + ask box use, plus manages the
  // keystore's DB providers (add/edit/delete) when the keystore is enabled.
  aiConfig?: AiConfig | null;
  aiProvider?: string | null;
  aiModel?: string | null;
  onSetAiProvider?: (provider: string | null) => void;
  onSetAiModel?: (model: string | null) => void;
  aiBackendOrigin?: string | null;
  aiToken?: string | null;
  onReloadAi?: () => void;
  // Agent instructions (backend + open project only) — write the project's
  // starter AGENTS.md (Settings → AI "Init" button). Absent when no backend
  // project is open.
  onInitAgentInstructions?: () => Promise<void>;
  version: string;
}

const HZ_CHOICES = [1, 2, 4, 8, 16];
const TABS = ['General', 'Engine', 'Connection', 'Storage', 'Cron', 'LLMs'] as const;
type Tab = (typeof TABS)[number];

export function SettingsModal(props: SettingsModalProps): JSX.Element | null {
  const [tab, setTab] = useState<Tab>('General');
  if (!props.isOpen) return null;

  // The Connection tab is only meaningful when wired to a backend; the
  // AI tab only when a backend is wired (the chat/ask surfaces are
  // backend-gated). Fall back to General so the strip never shows a dead tab.
  const hasConnection = !!props.apiLabel;
  const hasStorage = !!props.storageBackendOrigin;
  const hasCron = !!props.cronProjectId && !!props.cronBackendOrigin;
  const hasAi = !!props.aiConfig;
  const visibleTabs = TABS.filter(
    (t) =>
      (t !== 'Connection' || hasConnection) &&
      (t !== 'Storage' || hasStorage) &&
      (t !== 'Cron' || hasCron) &&
      (t !== 'LLMs' || hasAi),
  );
  const activeTab: Tab =
    (tab === 'Connection' && !hasConnection) ||
    (tab === 'Storage' && !hasStorage) ||
    (tab === 'Cron' && !hasCron) ||
    (tab === 'LLMs' && !hasAi)
      ? 'General'
      : tab;

  return (
    <Modal title="Settings" onClose={props.onClose} size="wide" flushBody>
      <div className="settings-modal">
        <div className="settings-tabs" role="tablist" aria-label="Settings sections">
          {visibleTabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={activeTab === t}
              className={`settings-tab${activeTab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="settings-body">
          {activeTab === 'General' && (
            <GeneralTab
              theme={props.theme}
              onToggleTheme={props.onToggleTheme}
              canReset={props.canReset}
              onReset={props.onReset}
              version={props.version}
            />
          )}
          {activeTab === 'Engine' && (
            <EngineTab
              hz={props.hz}
              onSetHz={props.onSetHz}
              stopServicesOnClose={props.stopServicesOnClose}
              onSetStopServices={props.onSetStopServices}
              workflowsAutostart={props.workflowsAutostart}
              onSetWorkflowsAutostart={props.onSetWorkflowsAutostart}
            />
          )}
          {activeTab === 'Connection' && hasConnection && (
            <ConnectionTab
              connectionStatus={props.connectionStatus}
              apiLabel={props.apiLabel!}
              onDisconnect={props.onDisconnect}
              backendOrigin={props.connectionBackendOrigin ?? null}
              token={props.storageToken ?? null}
            />
          )}
          {activeTab === 'Storage' && hasStorage && (
            <StorageTab
              backendOrigin={props.storageBackendOrigin ?? null}
              token={props.storageToken ?? null}
            />
          )}
          {activeTab === 'Cron' && hasCron && (
            <CronTab
              projectId={props.cronProjectId!}
              backendOrigin={props.cronBackendOrigin ?? null}
              token={props.cronToken ?? null}
            />
          )}
          {activeTab === 'LLMs' && hasAi && (
            <AiTab
              config={props.aiConfig!}
              provider={props.aiProvider ?? null}
              model={props.aiModel ?? null}
              onSetProvider={props.onSetAiProvider}
              onSetModel={props.onSetAiModel}
              backendOrigin={props.aiBackendOrigin ?? null}
              token={props.aiToken ?? null}
              onReload={props.onReloadAi}
              onInitAgentInstructions={props.onInitAgentInstructions}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

function GeneralTab({
  theme,
  onToggleTheme,
  canReset,
  onReset,
  version,
}: {
  theme: Theme;
  onToggleTheme: () => void;
  canReset: boolean;
  onReset: () => void;
  version: string;
}): JSX.Element {
  return (
    <div className="settings-section">
      <div className="settings-row">
        <div className="settings-row-label">
          <Desktop size={16} aria-hidden />
          <div>
            <div className="settings-row-title">Appearance</div>
            <div className="settings-row-hint">Light or dark theme.</div>
          </div>
        </div>
        <button
          type="button"
          className="settings-theme-toggle"
          onClick={onToggleTheme}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        >
          {theme === 'dark' ? <Moon size={15} aria-hidden /> : <Sun size={15} aria-hidden />}
          <span>{theme === 'dark' ? 'Dark' : 'Light'}</span>
        </button>
      </div>
      <div className="settings-row">
        <div className="settings-row-label">
          <ArrowCounterClockwise size={16} aria-hidden />
          <div>
            <div className="settings-row-title">Reset model</div>
            <div className="settings-row-hint">Return the model to step 0.</div>
          </div>
        </div>
        <button
          type="button"
          className="btn"
          onClick={onReset}
          disabled={!canReset}
        >
          Reset
        </button>
      </div>
      <div className="settings-row meta-row">
        <div className="settings-row-label">
          <Lightning size={16} aria-hidden />
          <div>
            <div className="settings-row-title">Version</div>
          </div>
        </div>
        <span className="settings-version">{version}</span>
      </div>
    </div>
  );
}

function EngineTab({
  hz,
  onSetHz,
  stopServicesOnClose,
  onSetStopServices,
  workflowsAutostart,
  onSetWorkflowsAutostart,
}: {
  hz: number;
  onSetHz: (hz: number) => void;
  stopServicesOnClose?: boolean | null;
  onSetStopServices?: (v: boolean | null) => void;
  workflowsAutostart?: boolean;
  onSetWorkflowsAutostart?: (v: boolean) => void;
}): JSX.Element {
  return (
    <div className="settings-section">
      <div className="settings-row">
        <div className="settings-row-label">
          <Clock size={16} aria-hidden />
          <div>
            <div className="settings-row-title">Tick rate</div>
            <div className="settings-row-hint">
              Ticks per second for the local engine / demo replay.
            </div>
          </div>
        </div>
      </div>
      <div className="settings-hz-choices" role="radiogroup" aria-label="Tick rate">
        {HZ_CHOICES.map((h) => (
          <button
            key={h}
            type="button"
            role="radio"
            aria-checked={hz === h}
            className={`settings-hz${hz === h ? ' active' : ''}`}
            onClick={() => onSetHz(h)}
          >
            {h} hz
          </button>
        ))}
      </div>
      {onSetStopServices && stopServicesOnClose !== undefined && (
        <div className="settings-row">
          <div className="settings-row-label">
            <Desktop size={16} aria-hidden />
            <div>
              <div className="settings-row-title">Stop services on close</div>
              <div className="settings-row-hint">
                When closing this project, automatically stop its docker
                services. Unset = ask each time.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="settings-theme-toggle"
            onClick={() =>
              onSetStopServices(
                stopServicesOnClose === null ? true
                  : stopServicesOnClose ? false
                    : null,
              )
            }
            aria-label="Toggle stop services on close"
          >
            <span>
              {stopServicesOnClose === null ? 'Ask' : stopServicesOnClose ? 'On' : 'Off'}
            </span>
          </button>
        </div>
      )}
      {onSetWorkflowsAutostart && workflowsAutostart !== undefined && (
        <div className="settings-row">
          <div className="settings-row-label">
            <Lightning size={16} aria-hidden />
            <div>
              <div className="settings-row-title">Autostart workflows</div>
              <div className="settings-row-hint">
                Automatically start this project's workflow worker when the
                project opens. Off = start it from the Workflows tab.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="settings-theme-toggle"
            onClick={() => onSetWorkflowsAutostart(!workflowsAutostart)}
            aria-label="Toggle autostart workflows"
          >
            <span>{workflowsAutostart ? 'On' : 'Off'}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function ConnectionTab({
  connectionStatus,
  apiLabel,
  onDisconnect,
  backendOrigin,
  token,
}: {
  connectionStatus?: ConnectionStatus;
  apiLabel: string;
  onDisconnect?: () => void;
  backendOrigin: string | null;
  token: string | null;
}): JSX.Element {
  const status = connectionStatus ?? 'disconnected';
  return (
    <div className="settings-section">
      <div className="settings-row">
        <div className="settings-row-label">
          <Plugs size={16} aria-hidden />
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            width: "500px"
          }}>
            <div className="settings-row-title">
              Backend connection 
              <sup style={{marginLeft:4}} className={`settings-conn-dot conn-${status}`} title={`Connection: ${status}`} aria-hidden>
                  ●
              </sup>
            </div>
            <span className='settings-copy-block' >  
              <code>{apiLabel}</code>
                
            </span>
          </div>
        </div>
        
      </div>
      <ContainerHostRow backendOrigin={backendOrigin} token={token} />
      {onDisconnect && (
        <div className="settings-row">
          <div className="settings-row-label">
            <div>
              <div className="settings-row-title">Disconnect</div>
              <div className="settings-row-hint">
                Close the WebSocket and return to the start page.
              </div>
            </div>
          </div>
          <button type="button" className="btn btn-danger" onClick={onDisconnect}>
            Disconnect
          </button>
        </div>
      )}
    </div>
  );
}

/** "Container Host" — the address the browser should use to reach services
 *  published on the docker host while the app runs in a container. Probed
 *  server-side (GET /api/connection/container-host runs the one-shot alpine
 *  `ip route` probe); rendered only when a backend HTTP origin is wired. */
function ContainerHostRow({
  backendOrigin,
  token,
}: {
  backendOrigin: string | null;
  token: string | null;
}): JSX.Element | null {
  type ProbeState =
    | { status: 'probing' }
    | { status: 'ok'; host: string }
    | { status: 'error'; error: string };
  const [state, setState] = useState<ProbeState>({ status: 'probing' });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!backendOrigin) return;
    let cancelled = false;
    setState({ status: 'probing' });
    (async () => {
      try {
        const res = await fetch(`${backendOrigin}/api/connection/container-host`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const j = (await res.json()) as { host?: string; detail?: unknown };
        if (cancelled) return;
        if (!res.ok) {
          const detail = typeof j?.detail === 'string' ? j.detail : `probe failed (${res.status})`;
          setState({ status: 'error', error: detail });
        } else if (typeof j?.host === 'string' && j.host) {
          setState({ status: 'ok', host: j.host });
        } else {
          setState({ status: 'error', error: 'probe returned no host' });
        }
      } catch (e) {
        if (!cancelled) setState({ status: 'error', error: (e as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [backendOrigin, token]);

  if (!backendOrigin) return null;

  const copy = async (): Promise<void> => {
    if (state.status !== 'ok') return;
    try {
      await navigator.clipboard.writeText(state.host);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — nothing to surface */
    }
  };

  return (
    <div className="settings-row">
      <div className="settings-row-label">
        <Desktop size={16} aria-hidden />
        <div>
          <div className="settings-row-title">Container Host</div>
          <div className="settings-row-hint">
            {state.status === 'probing' && 'Probing…'}
            {state.status === 'error' && <span className="settings-conn-status err">{state.error}</span>}
          </div>
        </div>
      </div>
      {state.status === 'ok' && (
        <button
          type="button"
          className="settings-copy-block"
          onClick={() => void copy()}
          title="Copy"
          aria-label="Copy container host address"
        >
          <code>{state.host}</code>
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
        </button>
      )}
    </div>
  );
}

function StorageTab({
  backendOrigin,
  token,
}: {
  backendOrigin: string | null;
  token: string | null;
}): JSX.Element {
  // The whole management surface lives in the shared panel (the Connect-API
  // wizard's step 2 embeds the same component): status card, installer link,
  // credential minting/paste, clear.
  return (
    <div className="settings-section">
      <VskiSetupPanel backendOrigin={backendOrigin} token={token} />
    </div>
  );
}

/** The chat-embeddings section: semantic search over saved chat history (the
 *  search_chats tool + vector recall in VSKI). Configured as its OWN
 *  OpenAI-compatible endpoint — base URL + optional key + model — so a local
 *  Ollama serves embeddings without any chat provider. Off by default.
 *  Mirrors the provider editor's layout (field rows + Test/Save). */
function EmbeddingsSection({
  backendOrigin,
  token,
}: {
  backendOrigin: string | null;
  token: string | null;
}): JSX.Element | null {
  const [cfg, setCfg] = useState<EmbeddingsConfig | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<EmbeddingsTestResult | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!backendOrigin) return;
    fetchEmbeddingsConfig(backendOrigin, token)
      .then((c) => {
        setCfg(c);
        setEnabled(c.enabled);
        // Prefill the common local defaults when unset (Ollama).
        setBaseUrl(c.baseUrl || 'http://localhost:11434/v1');
        setModel(c.model || 'nomic-embed-text');
      })
      .catch((e: Error) => setErr(e.message));
  }, [backendOrigin, token]);

  const dirty =
    cfg !== null &&
    (enabled !== cfg.enabled ||
      baseUrl.trim() !== cfg.baseUrl ||
      model.trim() !== cfg.model ||
      apiKey.trim().length > 0);

  const test = async (): Promise<void> => {
    if (!backendOrigin || testing || busy) return;
    setErr(null);
    setTestResult(null);
    if (!baseUrl.trim() || !model.trim()) {
      setErr('Base URL and model are required to test.');
      return;
    }
    setTesting(true);
    try {
      const res = await testEmbeddingsConfig(backendOrigin, token, {
        baseUrl: baseUrl.trim(),
        model: model.trim(),
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      });
      setTestResult(res);
    } catch (e) {
      setTestResult({ ok: false, detail: (e as Error).message });
    } finally {
      setTesting(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!backendOrigin || busy) return;
    setBusy(true);
    setErr(null);
    setTestResult(null);
    setSaved(false);
    try {
      const next = await saveEmbeddingsConfig(backendOrigin, token, {
        enabled,
        baseUrl: baseUrl.trim(),
        model: model.trim(),
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      });
      setCfg(next);
      setApiKey(''); // blank = keep the stored key on later saves
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!backendOrigin) return null;
  if (cfg === null && err === null) return null; // still loading

  return (
    <div className="ai-providers">
      <div className="ai-providers-head">
        <span className="ai-providers-title">
          <Sparkle size={13} aria-hidden /> Chat embeddings
        </span>
        <button
          type="button"
          className="settings-theme-toggle"
          onClick={() => setEnabled((v) => !v)}
          aria-label="Toggle chat embeddings"
          aria-pressed={enabled}
        >
          <span>{enabled ? 'On' : 'Off'}</span>
        </button>
      </div>
      <div className="ai-field-row">
        <label className="ai-field-label">Base URL</label>
        <input
          className="settings-input"
          value={baseUrl}
          placeholder="http://localhost:11434/v1"
          onChange={(e) => setBaseUrl(e.target.value)}
          spellCheck={false}
        />
      </div>
      <div className="ai-field-row">
        <label className="ai-field-label">Model</label>
        <input
          className="settings-input"
          value={model}
          placeholder="nomic-embed-text"
          onChange={(e) => setModel(e.target.value)}
          spellCheck={false}
        />
      </div>
      <div className="ai-field-row">
        <label className="ai-field-label">
          API key{cfg?.hasApiKey && <span className="ai-field-hint"> (stored — blank = keep)</span>}
        </label>
        <input
          className="settings-input"
          type="password"
          value={apiKey}
          placeholder={cfg?.hasApiKey ? '••••••' : 'optional for local Ollama'}
          onChange={(e) => setApiKey(e.target.value)}
        />
      </div>
      {err && <div className="ai-error">{err}</div>}
      {testResult && (
        <div className={`ai-test-result ${testResult.ok ? 'ok' : 'fail'}`}>
          {testResult.ok
            ? `✓ Endpoint responds — ${testResult.dimensions}-dimensional vectors.`
            : `✗ ${testResult.detail ?? 'failed'}`}
        </div>
      )}
      <div className="ai-editor-actions">
        <button
          type="button"
          className="btn"
          onClick={() => void test()}
          disabled={testing || busy}
          title="Embed a probe string to verify the endpoint before saving"
        >
          {testing ? 'Testing…' : 'Test'}
        </button>
        <button
          type="button"
          className="btn btn-start"
          onClick={() => void save()}
          disabled={busy || !dirty || (enabled && !(baseUrl.trim() && model.trim()))}
        >
          {busy ? 'Saving…' : saved ? 'Saved' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function AiTab({
  config,
  provider,
  model,
  onSetProvider,
  onSetModel,
  backendOrigin,
  token,
  onReload,
  onInitAgentInstructions,
}: {
  config: AiConfig;
  provider: string | null;
  model: string | null;
  onSetProvider?: (provider: string | null) => void;
  onSetModel?: (model: string | null) => void;
  backendOrigin: string | null;
  token: string | null;
  onReload?: () => void;
  onInitAgentInstructions?: () => Promise<void>;
}): JSX.Element {
  const confirm = useConfirm();
  // 'add' | {edit, provider} | null. Drives whether the editor replaces the list.
  const [editing, setEditing] = useState<{ mode: 'add' } | { mode: 'edit'; provider: AiProvider } | null>(null);
  const [busy, setBusy] = useState(false);
  const [mutError, setMutError] = useState<string | null>(null);

  const startAdd = (): void => {
    setMutError(null);
    setEditing({ mode: 'add' });
  };
  const startEdit = (p: AiProvider): void => {
    setMutError(null);
    setEditing({ mode: 'edit', provider: p });
  };
  const cancel = (): void => {
    setEditing(null);
    setMutError(null);
  };
  const saved = (): void => {
    setEditing(null);
    onReload?.();
  };

  const onDelete = async (p: AiProvider): Promise<void> => {
    setMutError(null);
    const ok = await confirm({
      title: `Delete provider "${p.label}"?`,
      body: 'The chat falls back to the remaining providers. This cannot be undone.',
      danger: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteAiProvider(backendOrigin!, token, p.id);
      onReload?.();
    } catch (e) {
      setMutError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // No providers configured at all → offer to add one. The form is always
  // available (the keystore auto-provisions a master key in local dev); no env
  // mechanics are surfaced here — if the backend can't accept writes it reports
  // that through the editor's inline error, not via proactive jargon text.
  if (!config.available || config.providers.length === 0) {
    return (
      <div className="settings-section">
        <div className="settings-row">
          <div className="settings-row-label">
            <Sparkle size={16} aria-hidden />
            <div>
              <div className="settings-row-title">No providers yet</div>
              <div className="settings-row-hint">
                Add a provider to enable the chat panel + ask box. API keys are
                AES-encrypted at rest on the backend.
              </div>
            </div>
          </div>
        </div>
        {mutError && <div className="ai-error">{mutError}</div>}
        {editing?.mode === 'add' ? (
          <ProviderEditor
            mode="add"
            presets={config.presets}
            backendOrigin={backendOrigin}
            token={token}
            onCancel={cancel}
            onSaved={saved}
          />
        ) : (
          <button type="button" className="btn btn-sm ai-add-btn" onClick={startAdd}>
            <Plus size={13} aria-hidden /> Add provider
          </button>
        )}
        <AgentInstructionsRow onInit={onInitAgentInstructions} />
        <EmbeddingsSection backendOrigin={backendOrigin} token={token} />
      </div>
    );
  }

  const selectedProvider =
    config.providers.find((p) => p.id === provider) ??
    config.providers.find((p) => p.id === config.default?.provider) ??
    config.providers[0];
  const models = selectedProvider.models.length ? selectedProvider.models : [selectedProvider.defaultModel];
  const selectedModel =
    model && models.includes(model) ? model : selectedProvider.defaultModel || models[0];

  return (
    <div className="settings-section">
      <div className="settings-row">
        <div className="settings-row-label">
          <Sparkle size={16} aria-hidden />
          <div>
            <div className="settings-row-title">Provider</div>
          </div>
        </div>
        <select
          className="settings-select"
          value={selectedProvider.id}
          onChange={(e) => {
            onSetProvider?.(e.target.value);
            // Reset model to the new provider's default on switch.
            const p = config.providers.find((x) => x.id === e.target.value);
            onSetModel?.(p?.defaultModel ?? p?.models[0] ?? null);
          }}
        >
          {config.providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
      <div className="settings-row">
        <div className="settings-row-label">
          <Sparkle size={16} aria-hidden />
          <div className="settings-row-title">Model</div>
        </div>
        <select
          className="settings-select"
          value={selectedModel}
          onChange={(e) => onSetModel?.(e.target.value)}
        >
          {models.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      <div className="ai-providers">
        <div className="ai-providers-head">
          <span className="ai-providers-title">
            <Key size={13} aria-hidden /> Providers
          </span>
          {editing === null && (
            <button type="button" className="btn btn-sm ai-add-btn" onClick={startAdd}>
              <Plus size={13} aria-hidden /> Add
            </button>
          )}
        </div>
        {mutError && <div className="ai-error">{mutError}</div>}
        {editing ? (
          <ProviderEditor
            mode={editing.mode}
            initial={editing.mode === 'edit' ? editing.provider : undefined}
            presets={config.presets}
            backendOrigin={backendOrigin}
            token={token}
            onCancel={cancel}
            onSaved={saved}
          />
        ) : (
          config.providers.map((p) => (
            <ProviderRow
              key={p.id}
              provider={p}
              busy={busy}
              backendOrigin={backendOrigin}
              token={token}
              onEdit={() => startEdit(p)}
              onDelete={() => void onDelete(p)}
            />
          ))
        )}
      </div>
      <EmbeddingsSection backendOrigin={backendOrigin} token={token} />
    
      <AgentInstructionsRow onInit={onInitAgentInstructions} />
    </div>
  );
}

/** The "init agent instructions" row — writes the open project's starter
 *  AGENTS.md (read by coding-agent harnesses AND the Lab chat's system
 *  prompt). Only rendered when a backend project is open (App omits the
 *  callback otherwise). A 409 ("already exists") surfaces inline — an
 *  existing file is never clobbered, by design. */
function AgentInstructionsRow({
  onInit,
}: {
  onInit?: () => Promise<void>;
}): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  if (!onInit) return null;

  const init = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    try {
      await onInit();
      setResult({ ok: true, text: '✓ AGENTS.md created in the project' });
    } catch (e) {
      setResult({ ok: false, text: `✗ ${(e as Error).message}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings-section ai-agent-instructions">
      <div className="settings-row">
        <div className="settings-row-label">
          <FileText size={16} aria-hidden />
          <div>
            <div className="settings-row-title">Agent instructions</div>
            <div className="settings-row-hint">
              Create the project's <code>AGENTS.md</code>.
            </div>
          </div>
        </div>
        <button type="button" className="btn" onClick={() => void init()} disabled={busy}>
          {busy ? 'Creating…' : 'Init'}
        </button>
      </div>
      {result && (
        <div className={`settings-conn-status${result.ok ? '' : ' err'}`}>{result.text}</div>
      )}
    </div>
  );
}
/** One provider in the list. Edit/delete are disabled for env-sourced (read-only)
 *  providers. The Test button pings the stored provider (key resolved server-side)
 *  and shows ok/fail inline — the most direct answer to "does this provider work?". */
function ProviderRow({
  provider,
  busy,
  backendOrigin,
  token,
  onEdit,
  onDelete,
}: {
  provider: AiProvider;
  busy: boolean;
  backendOrigin: string | null;
  token: string | null;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const editable = !provider.readonly;
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail?: string } | null>(null);

  const onTest = async (): Promise<void> => {
    setTesting(true);
    setTestResult(null);
    try {
      // No fields → the backend resolves them from the stored provider (id).
      const res = await testAiProvider(backendOrigin!, token, { provider: provider.id });
      setTestResult({ ok: res.ok, detail: res.detail });
    } catch (e) {
      setTestResult({ ok: false, detail: (e as Error).message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="ai-provider-row" data-locked={provider.readonly ? 'true' : 'false'}>
      <div className="ai-provider-meta">
        <div className="ai-provider-name">
          {provider.label}
          {provider.readonly && (
            <Lock size={11} className="ai-lock" aria-label="read-only (env provider)" />
          )}
        </div>
        <div className="ai-provider-sub">
          <code>{provider.id}</code> · {provider.defaultModel}
        </div>
        <div className="ai-provider-url" title={provider.baseUrl}>{provider.baseUrl}</div>
        {testResult && (
          <div className={`ai-test-result ${testResult.ok ? 'ok' : 'fail'}`}>
            {testResult.ok ? '✓ Connected' : `✗ ${testResult.detail ?? 'failed'}`}
          </div>
        )}
      </div>
      <div className="ai-provider-actions">
        <button
          type="button"
          className="icon-btn"
          onClick={() => void onTest()}
          disabled={testing}
          title="Test connection"
          aria-label={`Test ${provider.label} connection`}
        >
          <Plugs size={14} aria-hidden />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={onEdit}
          disabled={!editable}
          title={editable ? 'Edit' : 'Not editable (env provider)'}
          aria-label={`Edit ${provider.label}`}
        >
          <PencilSimple size={14} aria-hidden />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={onDelete}
          disabled={!editable || busy}
          title={editable ? 'Delete' : 'Not editable (env provider)'}
          aria-label={`Delete ${provider.label}`}
        >
          <Trash size={14} aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** Add/edit form. Add requires id + key; edit locks id and makes the key
 *  optional (blank = keep existing). Presets prefill the fields on add. */
function ProviderEditor({
  mode,
  initial,
  presets,
  backendOrigin,
  token,
  onCancel,
  onSaved,
}: {
  mode: 'add' | 'edit';
  initial?: AiProvider;
  presets: AiPreset[];
  backendOrigin: string | null;
  token: string | null;
  onCancel: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [id, setId] = useState(initial?.id ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [baseUrl, setBaseUrl] = useState(initial?.baseUrl ?? '');
  const [model, setModel] = useState(initial?.defaultModel ?? '');
  const [modelsText, setModelsText] = useState((initial?.models ?? []).join(','));
  const [apiKey, setApiKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail?: string } | null>(null);

  const applyPreset = (presetId: string): void => {
    const p = presets.find((x) => x.id === presetId);
    if (!p) return;
    setId((cur) => cur || p.id);
    setLabel(p.label);
    setBaseUrl(p.baseUrl);
    setModel(p.model);
    setModelsText(p.models.join(','));
  };

  const test = async (): Promise<void> => {
    setError(null);
    setTestResult(null);
    if (!baseUrl.trim() || !model.trim()) {
      setError('Base URL and model are required to test.');
      return;
    }
    // Add mode requires a key in the form; edit mode can fall back to the
    // stored key (send no apiKey → backend uses the stored one via provider id).
    if (mode === 'add' && !apiKey.trim()) {
      setError('Enter an API key to test.');
      return;
    }
    setTesting(true);
    try {
      const res = await testAiProvider(backendOrigin!, token, {
        // On edit, send the id so the backend fills in a blank key from storage.
        ...(mode === 'edit' ? { provider: initial!.id } : {}),
        baseUrl: baseUrl.trim(),
        model: model.trim(),
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      });
      setTestResult({ ok: res.ok, detail: res.detail });
    } catch (e) {
      setTestResult({ ok: false, detail: (e as Error).message });
    } finally {
      setTesting(false);
    }
  };

  const submit = async (): Promise<void> => {
    setError(null);
    const models = modelsText.split(',').map((s) => s.trim()).filter(Boolean);
    if (!baseUrl.trim() || !model.trim()) {
      setError('Base URL and model are required.');
      return;
    }
    if (mode === 'add' && (!id.trim() || !apiKey.trim())) {
      setError('ID and API key are required when adding.');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'add') {
        await createAiProvider(backendOrigin!, token, {
          id: id.trim(),
          label: label.trim() || id.trim(),
          baseUrl: baseUrl.trim(),
          apiKey: apiKey.trim(),
          model: model.trim(),
          models: models.length ? models : [model.trim()],
        });
      } else {
        await updateAiProvider(backendOrigin!, token, initial!.id, {
          label: label.trim() || undefined,
          baseUrl: baseUrl.trim(),
          model: model.trim(),
          models: models.length ? models : [model.trim()],
          ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        });
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ai-editor">
      <div className="ai-editor-title">{mode === 'add' ? 'Add provider' : `Edit ${initial?.label}`}</div>
      {mode === 'add' && presets.length > 0 && (
        <div className="ai-field-row">
          <label className="ai-field-label">Preset</label>
          <select className="settings-select" value="" onChange={(e) => applyPreset(e.target.value)}>
            <option value="">Choose a template…</option>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>
      )}
      <div className="ai-field-row">
        <label className="ai-field-label">ID</label>
        <input
          className="settings-input"
          value={id}
          disabled={mode === 'edit'}
          placeholder="e.g. gemini"
          onChange={(e) => setId(e.target.value)}
        />
      </div>
      <div className="ai-field-row">
        <label className="ai-field-label">Label</label>
        <input className="settings-input" value={label} placeholder="Display name" onChange={(e) => setLabel(e.target.value)} />
      </div>
      <div className="ai-field-row">
        <label className="ai-field-label">Base URL</label>
        <input className="settings-input" value={baseUrl} placeholder="https://…" onChange={(e) => setBaseUrl(e.target.value)} />
      </div>
      <div className="ai-field-row">
        <label className="ai-field-label">Model</label>
        <input className="settings-input" value={model} onChange={(e) => setModel(e.target.value)} />
      </div>
      <div className="ai-field-row">
        <label className="ai-field-label">Models (comma-separated)</label>
        <input
          className="settings-input"
          value={modelsText}
          placeholder="model-a, model-b"
          onChange={(e) => setModelsText(e.target.value)}
        />
      </div>
      <div className="ai-field-row">
        <label className="ai-field-label">
          API key{mode === 'edit' && <span className="ai-field-hint"> (blank = keep)</span>}
        </label>
        <input
          className="settings-input"
          type="password"
          value={apiKey}
          placeholder={mode === 'edit' ? '••••••' : 'paste key'}
          onChange={(e) => setApiKey(e.target.value)}
        />
      </div>
      {error && <div className="ai-error">{error}</div>}
      {testResult && (
        <div className={`ai-test-result ${testResult.ok ? 'ok' : 'fail'}`}>
          {testResult.ok ? '✓ Connected — provider responds.' : `✗ ${testResult.detail ?? 'failed'}`}
        </div>
      )}
      <div className="ai-editor-actions">
        <button type="button" className="btn" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button
          type="button"
          className="btn"
          onClick={() => void test()}
          disabled={testing || submitting}
          title="Send a one-token ping to verify the key, base URL, and model"
        >
          {testing ? 'Testing…' : 'Test'}
        </button>
        <button type="button" className="btn btn-start" onClick={() => void submit()} disabled={submitting}>
          {submitting ? 'Saving…' : mode === 'add' ? 'Add' : 'Save'}
        </button>
      </div>
    </div>
  );
}
